/**
 * O arquivo único do caso e o que a separação apurou dele (E97.F4.T01/T02).
 *
 * Espelha `ArquivoUnicoRecebido` e `SeparacaoApurada` de
 * `apps/api/types/separacao_do_arquivo.py` (há teste de paridade dos `Literal`).
 * Vem no `GET /exame-eletronico` porque a listagem de documentos omite o
 * arquivo de origem: os documentos contidos já são os documentos do caso.
 * Módulo próprio porque `exame_eletronico.ts` já passou do teto de linhas — e
 * por isso o campo novo do caso também mora aqui (`ExameEletronicoCasoComArquivosData`).
 */
import type { ExameEletronicoCasoData, ResultadoExameEletronicoData } from '@/lib/exame_eletronico';

export const TIPOS_DO_CONTIDO = [
  'ccb',
  'termo_inss',
  'comprovante',
  'laudo',
  'fotografia',
  'cadastro',
  'cet',
  'proposta_de_seguro',
  'termo_de_servico',
  'cnh',
  'crlv',
  // E128.F1: os formulários do arquivo único do cartão consignado.
  'termo_de_adesao',
  'termo_de_consentimento',
  'termo_de_desbloqueio',
] as const;
export type TipoDoContido = (typeof TIPOS_DO_CONTIDO)[number];

export interface DocumentoContidoData {
  tipo: TipoDoContido;
  paginas: number[];
}

export interface MotivoDaReprovacaoData {
  codigo: string;
  detalhe: string;
  paginas: number[];
}

export interface SeparacaoApuradaData {
  situacao: 'conferida' | 'reprovada';
  total_de_paginas: number;
  documentos: DocumentoContidoData[];
  motivos: MotivoDaReprovacaoData[];
}

export interface ArquivoUnicoRecebidoData {
  documento_id: string;
  nome_do_arquivo: string;
  recebido_em: string;
  separacao: SeparacaoApuradaData;
}

/** "12" · "13 e 14" · "1 a 12" · "15, 19 e 20" — como as páginas se escrevem numa frase. */
export function faixaDePaginas(paginas: readonly number[]): string {
  if (paginas.length === 0) return '';
  if (paginas.length === 1) return String(paginas[0]);
  const primeira = paginas[0] as number;
  const ultima = paginas[paginas.length - 1] as number;
  const seguidas = paginas.every((pagina, i) => pagina === primeira + i);
  if (seguidas && paginas.length > 2) return `${primeira} a ${ultima}`;
  return `${paginas.slice(0, -1).join(', ')} e ${ultima}`;
}

/** O `GET /exame-eletronico` com o campo da E97.F4. */
export type ExameEletronicoCasoComArquivosData = ExameEletronicoCasoData & {
  arquivos_unicos?: ArquivoUnicoRecebidoData[];
};

/** Os arquivos únicos do caso; payload anterior à E97.F4 (ou caso ainda não carregado) vale vazio. */
export function arquivosUnicosDoCaso(
  caso: ExameEletronicoCasoComArquivosData | null,
): readonly ArquivoUnicoRecebidoData[] {
  return caso?.arquivos_unicos ?? [];
}

/** E97.F1 — de qual arquivo único e páginas o documento verificado saiu. */
export interface OrigemDoArquivoData {
  documento_id: string;
  nome_do_arquivo: string;
  sha256?: string | null;
  paginas: number[];
}

/** O resultado do exame com o campo da E97.F1 (fora do tipo base, que já passou do teto). */
export type ResultadoComOrigemData = ResultadoExameEletronicoData & {
  origem?: OrigemDoArquivoData | null;
};

/** A origem do documento verificado; documento que chegou sozinho (ou exame anterior) vale `null`. */
export function origemDoResultado(resultado: ResultadoComOrigemData): OrigemDoArquivoData | null {
  return resultado.origem ?? null;
}
